import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TYPESOFORDERS, CURRENCY } from "../../../contants";
import { deleteLocalOrder } from "../../../redux/action/orderActions";
import { deleteLocalOrderOffline } from "../../../redux/action/offlineDataActions";
import haptics from "../../../helpers/haptics";
import { getCompleteOrderTotals, getRoundOffType, getTableTypeFromOrder } from "../helpers/orderHelpers";
import roundOffTotal from "../../../helpers/roundOffTotal";
import calculateOrderTotals from "../../../helpers/calculateOrderTotals";

const MobileTableView = ({
  tables,
  activeOrders,
  handleTableNumberClick,
  handleOtherOrderClick,
  selectedOrderTypeId,
  showAllOrders = false,
}) => {
  const dispatch = useDispatch();
  const [selectedTableType, setSelectedTableType] = useState("all");
  const [selectedOrderFilter, setSelectedOrderFilter] = useState("all");
  const { activeOrder } = useSelector((state) => state.order);
  const { isOnline, isAppOffline } = useSelector((state) => state.networkStatus);
  const isElectron = window?.api?.isElectron;

  const { exclusiveGST, lowerRoundOff, upperRoundOff } = useSelector((state) => state.util);
  const { cgst, sgst } = useSelector((state) => state.user);
  const roundOffType = getRoundOffType(lowerRoundOff, upperRoundOff);

  const checkActive = (tableNumber) => {
    return activeOrders.find((table) => tableNumber === table.tableNumber);
  };

  const getTableTypes = () => {
    const key = "tableType";
    const arrayUniqueByKey = [
      ...new Map(tables.map((item) => [item[key], item])).values(),
    ];
    return arrayUniqueByKey;
  };

  const getFilteredTables = () => {
    if (selectedTableType === "all") {
      return tables;
    } else {
      return tables.filter((tab) => tab.tableTypeId === selectedTableType);
    }
  };

  const getParcelOrders = () => {
    return activeOrders.filter(
      (order) => order.orderTypeId === TYPESOFORDERS[1].value
    );
  };

  const getHomeDeliveryOrders = () => {
    return activeOrders.filter(
      (order) => order.orderTypeId === TYPESOFORDERS[2].value
    );
  };

  const handleTableClick = (table) => {
    const activeTable = checkActive(table.tableNumber);
    if (activeTable) {
      handleOtherOrderClick(activeTable);
    } else {
      handleTableNumberClick(table);
    }
  };

  const handleDeleteOrder = (e, order) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Check if order has items - silently return
    if (order.orderItems && order.orderItems.length > 0) {
      return;
    }
    
    // Delete the order without confirmation
    if ((isAppOffline || !isOnline) && isElectron) {
      dispatch(deleteLocalOrderOffline(order.refId));
    } else {
      dispatch(deleteLocalOrder(order.refId));
    }
  };

  const getFilteredOrdersByType = () => {
    if (selectedOrderFilter === "all") {
      return activeOrders;
    } else {
      return activeOrders.filter((order) => order.orderTypeId === selectedOrderFilter);
    }
  };

  const renderAllOrders = () => {
    const filteredOrders = getFilteredOrdersByType();
    
    if (filteredOrders.length === 0) {
      return (
        <div className="mobile-empty-state">
          <i className="mdi mdi-receipt"></i>
          <p className="mb-0">No orders</p>
        </div>
      );
    }

    return (
      <div>
        {filteredOrders.map((order, index) => {
          // Calculate tableKind for THIS specific order
          const orderTableKind = getTableTypeFromOrder(tables, order);
          const orderTotals = getCompleteOrderTotals(order, cgst, sgst, orderTableKind, exclusiveGST, roundOffType, calculateOrderTotals, roundOffTotal);
          const isActive = order.refId === activeOrder;
          const canDelete = !order.orderItems || order.orderItems.length === 0;
          const orderClass = order.orderTypeId === TYPESOFORDERS[0].value 
            ? "dine-in" 
            : order.orderTypeId === TYPESOFORDERS[1].value 
            ? "parcel" 
            : "delivery";
          
          return (
            <div
              key={index}
              className={`mobile-order-card ${isActive ? "active" : orderClass}`}
              onClick={() => handleOtherOrderClick(order)}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="flex-fill">
                  <div className="fw-semibold fs-6 mobile-order-card-text">
                    {order.tableNumber ? `Table ${order.tableNumber}` : `${order.orderType} ${order.customerName ? `- (${order.customerName})` : ""}`}
                  </div>
                  <small className="mobile-order-card-subtitle">
                    {order.branchOrderNumber} • {order.orderItems?.length || 0} items
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="text-end">
                    <div className="fw-bold fs-6 mobile-order-card-text">
                      {CURRENCY}{orderTotals?.roundedGrandTotal}
                    </div>
                    {isActive && (
                      <span className="badge text-white mt-1 mobile-order-badge-active">
                        Active
                      </span>
                    )}
                  </div>
                  
                  {/* Delete Button - Inline after price */}
                  {canDelete && (
                    <button
                      className="mobile-order-delete-btn-inline"
                      onClick={(e) => handleDeleteOrder(e, order)}
                      title="Delete order"
                    >
                      <i className="mdi mdi-close-circle"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTableLayout = () => {
    // If showing all orders view
    if (showAllOrders) {
      return renderAllOrders();
    }

    const filteredTables = getFilteredTables();

    // Dine-in table layout
    return (
      <div className="mobile-tables-grid">
        {filteredTables.map((table, index) => {
          const isReserved = Boolean(checkActive(table.tableNumber));
          const activeTableOrder = checkActive(table.tableNumber);
          const isActiveTable = activeTableOrder && activeTableOrder.refId === activeOrder;
          
          const circleClass = isActiveTable ? 'active' : isReserved ? 'reserved' : 'available';
          
          return (
            <div key={index} className="mobile-table-cell">
              <div
                className="mobile-table-wrapper cursor-pointer"
                onClick={() => handleTableClick(table)}
              >
                <div className="mobile-table-chairs">
                  <div className="mobile-chair top">
                    <img src={'/assets/images/dashboard/chairIcon.svg'} alt="chair top" />
                  </div>
                  <div className="mobile-chair right">
                    <img src={'/assets/images/dashboard/chairIcon.svg'} alt="chair right" />
                  </div>
                  <div className="mobile-chair bottom">
                    <img src={'/assets/images/dashboard/chairIcon.svg'} alt="chair bottom" />
                  </div>
                  <div className="mobile-chair left">
                    <img src={'/assets/images/dashboard/chairIcon.svg'} alt="chair left" />
                  </div>
                </div>
                <div className={`mobile-table-number-circle ${circleClass}`}>
                  {table.tableNumber}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const tableTypes = getTableTypes();

  const orderFilterTabs = [
    { value: "all", label: "All", icon: "mdi mdi-view-grid-outline" },
    { value: TYPESOFORDERS[0].value, label: "Dine-in", icon: "mdi mdi-silverware-fork-knife" },
    { value: TYPESOFORDERS[1].value, label: "Parcel", icon: "mdi mdi-package-variant" },
    { value: TYPESOFORDERS[2].value, label: "Delivery", icon: "mdi mdi-bike" },
  ];

  return (
    <div className="mobile-table-view-container">
      {/* Order Type Filter Tabs - Shows when viewing all orders */}
      {showAllOrders && (
        <div className="mobile-order-filter-tabs mb-3">
          {orderFilterTabs.map((tab) => (
            <button
              key={tab.value}
              className={`mobile-order-filter-tab ${
                selectedOrderFilter === tab.value ? "active" : ""
              }`}
              onClick={() => {
                haptics.medium();
                setSelectedOrderFilter(tab.value);
              }}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Table Type Filter - Shows for dine-in tables */}
      {tableTypes.length > 0 && !showAllOrders && (
        <div className="mobile-table-types-container d-flex align-items-center justify-content-left">
          <button
            className={`btn btn-sm d-flex shadow-none align-items-center justify-content-center py-1 mobile-table-type-filter-btn ${
              selectedTableType === "all" ? "btn-dark text-white" : "border-2 border-dark"
            }`}
            onClick={() => {
              haptics.medium();
              setSelectedTableType("all");
            }}
          >
            <span className="text-md">All</span>
          </button>
          {tableTypes.map((type, index) => (
            <button
              key={index}
              className={`btn btn-sm d-flex shadow-none align-items-center justify-content-center py-1 mobile-table-type-filter-btn ${
                selectedTableType === type.tableTypeId ? "btn-dark text-white" : "border-2 border-dark"
              }`}
              onClick={() => {
                haptics.medium();
                setSelectedTableType(type.tableTypeId);
              }}
            >
              <span>{type.tableType}</span>
            </button>
          ))}
        </div>
      )}

      <div className="mobile-table-layout-container">
        {renderTableLayout()}

        {selectedOrderTypeId === TYPESOFORDERS[0].value && !showAllOrders && (
          <div className="mobile-table-legend">
            <div className="d-flex align-items-center gap-2">
              <div className="bg-dark rounded mobile-legend-box"></div>
              <small className="text-muted">Reserved</small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="bg-light border rounded mobile-legend-box"></div>
              <small className="text-muted">Available</small>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileTableView;